import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static targets = ["steps", "stepsPanel", "metadata", "progressBar", "status", "videoContainer", "downloadBtn", "trackAnalyzing", "trackAssembling", "trackReady", "renderLabel", "renderSub", "renderActions", "viewBtn"];
  static values = {
    renderId: Number,
    retryUrl: String,
    projectUrl: String,
    i18nAnalyzingLabel: String,
    i18nAnalyzingSub: String,
    i18nEnhancingLabel: String,
    i18nEnhancingSub: String,
    i18nAssemblingLabel: String,
    i18nAssemblingSub: String,
    i18nReadyLabel: String,
    i18nUpdatingLabel: String,
    i18nUpdatingSub: String,
    i18nPreparingLabel: String,
    i18nNormalizeTitle: { type: String, default: "Normalize assets" },
    i18nNormalizeDetail: { type: String, default: "{done} of {total} done" },
    i18nVideoAriaLabel: { type: String, default: "Generated video" },
  };

  connect() {
    if (!this.hasRenderIdValue) return;
    this.subscription = consumer.subscriptions.create({ channel: "RenderChannel", render_id: this.renderIdValue }, { received: (data) => this.handleUpdate(data) });
    // Sync state immediately so Turbo cache restoration doesn't show stale phase
    if (this.hasTrackAnalyzingTarget) this.#fetchCurrentState();
  }

  disconnect() {
    this.subscription?.unsubscribe();
  }

  handleUpdate(data) {
    const status = data.project_status || data.status;
    if (!status || this._terminal) return;

    // ── Render screen (new show.html.erb render state) ────────────────────
    if (this.hasTrackAnalyzingTarget) {
      this.#updateRenderScreen(status, data);
      return;
    }

    // ── Legacy output page targets ────────────────────────────────────────
    if (status && this.hasStatusTarget) {
      this.statusTarget.textContent = status.charAt(0).toUpperCase() + status.slice(1);
      this.statusTarget.className = `badge badge--${this.#badgeClass(status)}`;
    }

    if (data.progress_percent !== undefined && this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress_percent}%`;
    }

    const terminal = status === "completed" || status === "failed";
    if (data.steps && this.hasStepsTarget && !terminal) {
      this.stepsTarget.innerHTML = this.#renderSteps(data.steps);
    }

    if (terminal) {
      this._terminal = true;
      window.location.reload();
    }
  }

  async #fetchCurrentState() {
    if (!this.hasProjectUrlValue) return;
    try {
      const resp = await fetch(`${this.projectUrlValue}.json`, { headers: { Accept: "application/json" } });
      if (!resp.ok) return;
      const data = await resp.json();
      const projectStatus = data.render_step || data.status;
      if (projectStatus) this.handleUpdate({ project_status: projectStatus });
    } catch (_) {}
  }

  #updateRenderScreen(status, data = {}) {
    const sub = data.detail || null;

    if (status === "bgm_update") {
      this.#setTrack("analyzing");
      this.#setLabel(this.i18nUpdatingLabelValue, sub || this.i18nUpdatingSubValue);
    } else if (status === "bgm_finalizing") {
      this.#setTrack("assembling");
      this.#setLabel(this.i18nUpdatingLabelValue, sub || this.i18nUpdatingSubValue);
    } else if (status === "analyzing") {
      this.#setTrack("analyzing");
      this.#setLabel(this.i18nAnalyzingLabelValue, sub || this.i18nAnalyzingSubValue);
    } else if (status === "enhancing" || status === "writing") {
      this.#setTrack("analyzing");
      this.#setLabel(this.i18nEnhancingLabelValue, sub || this.i18nEnhancingSubValue);
    } else if (status === "assembling" || status === "compositing") {
      this.#setTrack("assembling");
      this.#setLabel(this.i18nAssemblingLabelValue, sub || this.i18nAssemblingSubValue);
    } else if (status === "completed") {
      this.#setTrack("ready");
      this.#setLabel(this.i18nReadyLabelValue, "");
      if (this.hasRenderActionsTarget) this.renderActionsTarget.classList.add("visible");
      if (this.hasRenderLabelTarget) this.renderLabelTarget.classList.remove("pulsing");
      const destination = data.redirect || this.projectUrlValue;
      if (this.hasViewBtnTarget) this.viewBtnTarget.href = destination;
      setTimeout(() => {
        if (destination) window.location.href = destination;
      }, 3000);
    } else if (status === "failed") {
      this._terminal = true;
      window.location.reload();
    }
  }

  #setTrack(active) {
    const steps = { analyzing: 0, assembling: 1, ready: 2 };
    const activeIdx = steps[active] ?? 0;
    const targets = ["trackAnalyzing", "trackAssembling", "trackReady"];
    targets.forEach((t, i) => {
      if (this[`has${t.charAt(0).toUpperCase() + t.slice(1)}Target`]) {
        this[`${t}Target`].className = "render-step" + (i <= activeIdx ? " active" : "");
      }
    });
  }

  #setLabel(label, sub) {
    if (this.hasRenderLabelTarget) this.renderLabelTarget.textContent = label;
    if (this.hasRenderSubTarget && sub !== undefined) this.renderSubTarget.textContent = sub;
  }

  // ---- Private helpers ----

  #renderSteps(steps) {
    const normalizeSteps = steps.filter((s) => s.type === "normalize");
    const otherSteps = steps.filter((s) => s.type !== "normalize");

    const normTotal = normalizeSteps.length;
    const normDone = normalizeSteps.filter((s) => s.status === "completed").length;
    const normActive = normalizeSteps.some((s) => s.status === "active");
    const normStatus = normDone === normTotal && normTotal > 0 ? "completed" : normActive ? "active" : "pending";
    const normPct = normTotal > 0 ? Math.round((normDone * 100) / normTotal) : 0;

    const normalizeRow =
      normTotal === 0
        ? ""
        : `
      <div class="render-panel__step">
        <svg class="render-panel__step-icon render-panel__step-icon--${normStatus}"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          ${normStatus === "completed" ? '<polyline points="20 6 9 17 4 12"/>' : '<circle cx="12" cy="12" r="10"/>'}
        </svg>
        <div style="flex:1;">
          <div class="render-panel__step-title">${this.i18nNormalizeTitleValue}</div>
          <div class="render-panel__step-detail">${this.i18nNormalizeDetailValue.replace("{done}", normDone).replace("{total}", normTotal)}</div>
          <div class="render-panel__step-bar">
            <div class="render-panel__step-bar-fill" style="width:${normPct}%;"></div>
          </div>
        </div>
      </div>`;

    const otherRows = otherSteps
      .map(
        (step) => `
      <div class="render-panel__step">
        <svg class="render-panel__step-icon render-panel__step-icon--${step.status}"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          ${step.status === "completed" ? '<polyline points="20 6 9 17 4 12"/>' : '<circle cx="12" cy="12" r="10"/>'}
        </svg>
        <div>
          <div class="render-panel__step-title">${this.#humanize(step.type)}</div>
          <div class="render-panel__step-detail">${step.detail || ""}</div>
        </div>
      </div>`,
      )
      .join("");

    return normalizeRow + otherRows;
  }

  #badgeClass(status) {
    const map = {
      completed: "success",
      rendering: "warning",
      processing: "warning",
      failed: "error",
      draft: "info",
    };
    return map[status] || "info";
  }

  #humanize(str) {
    return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  #escape(value) {
    if (value === null || value === undefined) return "";
    return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  #playerHTML(videoUrl) {
    return `
      <div class="player" data-controller="videojs" data-videojs-fill-value="true">
        <video class="video-js vjs-big-play-centered"
               data-videojs-target="video"
               controls preload="metadata" playsinline
               aria-label="${this.i18nVideoAriaLabelValue}">
          <source src="${this.#escape(videoUrl)}" type="video/mp4">
        </video>
      </div>
    `;
  }
}
